/**
 * React hook that wires the MQTT telemetry singleton to all status hooks.
 * Collects SIP, connectivity, battery, and permission state into heartbeats.
 * Sends alerts on critical state transitions.
 * Handles session enforcement (single-device login).
 */
import { useEffect, useRef, useState, useCallback, useSyncExternalStore } from 'react';
import { useAuth } from './useAuth';
import { useSip } from './useSip';
import { useConnectivity } from './useConnectivity';
import { useBattery } from './useBattery';
import { usePermissions } from './usePermissions';
import { NativeBridge } from '../native/NativeBridge';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import * as MqttTelemetry from '../services/MqttTelemetry';
import { connectWebSocketCommands, disconnectWebSocketCommands } from '../services/WebSocketCommands';
import { setDeviceStateSnapshot } from '../services/connectivityHeartbeat';

const MQTT_BROKER_URL = (import.meta as any).env?.VITE_MQTT_BROKER_URL || '';
const MQTT_USERNAME = (import.meta as any).env?.VITE_MQTT_USERNAME || '';
const MQTT_PASSWORD = (import.meta as any).env?.VITE_MQTT_PASSWORD || '';
const APP_VERSION = (import.meta as any).env?.VITE_APP_VERSION || '1.0.0';
/** Single-device enforcement — see handleSessionConflict. Off unless explicitly set. */
const SESSION_ENFORCEMENT = (import.meta as any).env?.VITE_MQTT_SESSION_ENFORCEMENT === 'true';

/**
 * ── T-6.01: DE BROKERGEGEVENS KOMEN VAN DE SERVER, NIET UIT DE BUNDLE ──────
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet/plan.md §14 (fase 6).
 *
 * De productiebundle heeft een LEGE `VITE_MQTT_BROKER_URL` — `deploy.yml` schrijft een
 * `.env.production` met alleen `VITE_API_URL` en `VITE_MEDIA_URL`. Het effect hieronder
 * bailde daarop, en samen met de (inmiddels herstelde) native-uitschakeling van socket.io
 * had de APK NUL commandokanalen.
 *
 * ⚠ De voor de hand liggende "oplossing" — `VITE_MQTT_USERNAME` en `VITE_MQTT_PASSWORD` in
 * `.env.production` — mag NIET. Een Vite-bundle is publiek: alles wat `import.meta.env`
 * heet, staat leesbaar in de JavaScript die elke browser en elke APK-extractie oplevert. Een
 * brokerwachtwoord daarin is een brokerwachtwoord dat iedereen heeft die één `.js`-bestand
 * opent, en die broker draagt de commando's van 800+ tablets — `open_door` incluis.
 *
 * Daarom haalt de app ze op achter het device token, bij het opstarten. De env-waarden
 * blijven bestaan als ONTWIKKELterugval: lokaal draait geen echte API met deze instellingen,
 * en een ontwikkelaar hoort niet eerst een server te moeten configureren.
 *
 * ⚠ Zonder `MQTT_WS_URL` / `MQTT_TABLET_USER` / `MQTT_TABLET_PASS` op de SERVER antwoordt de
 * route `enabled: false` en blijft de tablet op socket.io. Dat werkt vandaag al; deze hele
 * feature is bewust zo ontworpen dat hij ZONDER MQTT werkt (persist + WS + poll). Dit is een
 * optimalisatie, geen voorwaarde.
 */
interface RuntimeMqttConfig {
  brokerUrl: string;
  username: string;
  password: string;
}

export function useMqttTelemetry() {
  const { user, isAuthenticated, logout, api } = useAuth();
  const [runtimeMqtt, setRuntimeMqtt] = useState<RuntimeMqttConfig | null>(null);

  // Eén keer per sessie ophalen. Faalt hij, dan blijft `runtimeMqtt` null en valt alles
  // terug op de env-waarden — die in productie leeg zijn, dus dan is er simpelweg geen MQTT
  // en draait de tablet op socket.io. Er is geen retry: een tablet die dit mist, mist een
  // versnelling en geen functie.
  useEffect(() => {
    if (!isAuthenticated) {
      setRuntimeMqtt(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res: any = await api.get('/api/v1/resident/runtime-config');
        const mqtt = res && res.mqtt;
        if (cancelled || !mqtt || mqtt.enabled !== true) return;
        if (!mqtt.brokerUrl || !mqtt.username || !mqtt.password) return;
        setRuntimeMqtt({
          brokerUrl: String(mqtt.brokerUrl),
          username: String(mqtt.username),
          password: String(mqtt.password),
        });
      } catch {
        /* geen runtime-config — socket.io draagt de commando's */
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, api]);

  const brokerUrl = runtimeMqtt ? runtimeMqtt.brokerUrl : MQTT_BROKER_URL;
  const brokerUser = runtimeMqtt ? runtimeMqtt.username : MQTT_USERNAME;
  const brokerPass = runtimeMqtt ? runtimeMqtt.password : MQTT_PASSWORD;

  const { sipState, sipExtension } = useSip();
  const { layers } = useConnectivity();
  const { battery } = useBattery();
  const { permissions, allGranted } = usePermissions();
  const [macAddress, setMacAddress] = useState('unknown');
  const [sessionConflict, setSessionConflict] = useState<string | null>(null);

  // Fetch device MAC address on mount (informational only, not used in topics)
  useEffect(() => {
    KeepAliveBridge.getDeviceMac().then(mac => {
      console.log('[MQTT] Device MAC/ID:', mac);
      setMacAddress(mac);
    });
  }, []);

  // Subscribe to MQTT connection state for re-renders
  const connectionState = useSyncExternalStore(
    MqttTelemetry.subscribe,
    MqttTelemetry.getConnectionState,
  );

  // Track previous states for alert detection
  const prevSipRef = useRef(sipState);
  const prevNetworkRef = useRef(layers.network);

  // Store connection type from native bridge
  const connectionTypeRef = useRef('unknown');

  // Periodically get connection type from native bridge
  useEffect(() => {
    const update = async () => {
      try {
        const status = await NativeBridge.getNetworkStatus();
        connectionTypeRef.current = status.connectionType;
      } catch { /* ignore */ }
    };
    update();
    const interval = setInterval(update, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Set up the data collector so the singleton can gather status each heartbeat
  useEffect(() => {
    const missing: string[] = [];
    for (const [key, value] of Object.entries(permissions)) {
      if (value === 'denied' || value === 'prompt') {
        missing.push(key);
      }
    }

    // Mirror the same state onto the connectivity heartbeat, which is the only
    // telemetry that actually reaches the portal. The MQTT payload below is
    // published to caro/device/<userId>/heartbeat, a topic the API does not
    // subscribe to (it listens on caro/device/heartbeat/<mac> for the hub
    // fleet) — and in production MQTT never connects at all. So this line, not
    // the collector, is what puts battery/permissions/extension in front of an
    // operator.
    setDeviceStateSnapshot({
      // Only when the platform actually reports one: useBattery defaults to
      // level 100 / charging false when the Battery API is missing, and
      // reporting that verbatim would show a healthy full battery for a device
      // whose charge we cannot see at all.
      batteryLevel: battery.supported ? battery.level : undefined,
      batteryCharging: battery.supported ? battery.charging : undefined,
      sipExtension: sipExtension || undefined,
      connectionType: connectionTypeRef.current,
      permissionsOk: allGranted,
      permissionsMissing: missing,
    });

    MqttTelemetry.setStatusDataCollector(() => {
      let status: 'ok' | 'degraded' | 'error' = 'ok';
      if (!layers.network) status = 'error';
      else if (!layers.api || sipState !== 'registered' || !allGranted) status = 'degraded';

      return {
        status,
        services: {
          mqtt: { status: 'running' },
          network: { status: layers.network ? 'ok' : 'down' },
          sip: { status: sipState === 'registered' ? 'running' : sipState, extension: sipExtension || undefined },
        },
        system: {
          battery_level: battery.level,
          battery_charging: battery.charging,
          connection_type: connectionTypeRef.current,
        },
        permissions: {
          all_granted: allGranted,
          missing,
        },
      };
    });
  }, [sipState, sipExtension, layers, battery, permissions, allGranted]);

  // Handle session conflict — another device logged in as the same user.
  //
  // The forced logout is OPT-IN and off by default. It rides on MQTT, which
  // never connected in production (no broker URL in the build), so this code
  // has never actually run on the fleet. Switching MQTT on would have silently
  // armed it fleet-wide: every device publishes a session_claim on connect, and
  // any OTHER device on the same userId with a different macAddress logs itself
  // out 3s later — two tablets in one apartment would kick each other in a loop
  // on every reconnect. Enabling it is a deliberate fleet decision, not a
  // side effect of turning the broker on. Log the conflict either way.
  const handleSessionConflict = useCallback((reason: string) => {
    if (!SESSION_ENFORCEMENT) {
      console.warn('[MQTT] Session conflict (enforcement disabled, staying logged in):', reason);
      return;
    }
    console.warn('[MQTT] Session conflict detected, logging out:', reason);
    setSessionConflict(reason);
    setTimeout(() => {
      MqttTelemetry.disconnect();
      logout();
    }, 3000);
  }, [logout]);

  // Register session conflict listener
  useEffect(() => {
    const unsubscribe = MqttTelemetry.onSessionConflict(handleSessionConflict);
    return unsubscribe;
  }, [handleSessionConflict]);

  // Connect when authenticated + a broker is known, disconnect on logout.
  //
  // "Een broker is bekend" komt uit de runtime-config (productie) of uit de env
  // (ontwikkeling) — zie de noot boven deze hook.
  const hasConnectedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !brokerUrl) {
      if (hasConnectedRef.current) {
        MqttTelemetry.sendAlert({
          type: 'logout',
          severity: 'warning',
          message: 'User logged out',
          service: 'app',
        });
        MqttTelemetry.disconnect();
        hasConnectedRef.current = false;
      }
      return;
    }

    if (hasConnectedRef.current) return;

    // Topics are keyed on user.id, but auto-provisioned device residents often
    // have no linked User account (user.id is null) — fall back to personId so
    // the tablet still connects and the automation engine (which uses the same
    // userId||personId fallback) can reach it.
    const userId = user?.id ?? (user as any)?.personId ?? (user as any)?.person?.id;
    if (!userId) return;

    hasConnectedRef.current = true;
    MqttTelemetry.connect({
      brokerUrl,
      username: brokerUser,
      password: brokerPass,
      userId: Number(userId),
      macAddress,
      sipExtension: sipExtension || 'unknown',
      appVersion: APP_VERSION,
      userName: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user as any)?.username || 'Unknown',
      locationId: (user as any)?.locationId,
      locationName: (user as any)?.locationName,
    });
  }, [isAuthenticated, user, macAddress, sipExtension, brokerUrl, brokerUser, brokerPass]);

  // Socket.IO command channel — connected on EVERY platform, native included.
  //
  // It used to be `if (isNative) return` ("Android uses MQTT"), but the
  // production bundle has no MQTT: deploy.yml writes a .env.production with
  // only VITE_API_URL + VITE_MEDIA_URL, so VITE_MQTT_BROKER_URL is empty and
  // the MQTT effect above bails at `!MQTT_BROKER_URL`. That left the APK with
  // ZERO command channels — refresh-tablets, show_message, TTS, remote
  // emergency and display_settings all silently did nothing, while the same
  // build in a browser worked because it fell through to this socket.
  //
  // Running both channels is safe: the API publishes the SAME command id to
  // socket.io and MQTT (locationController.pushCommandToLocation), and
  // useMqttCommands dedups on cmd.id, so a device reached twice acts once.
  const wsConnectedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) {
      if (wsConnectedRef.current) {
        disconnectWebSocketCommands();
        wsConnectedRef.current = false;
      }
      return;
    }

    const userId = user?.id ?? (user as any)?.personId ?? (user as any)?.person?.id;
    if (!userId || wsConnectedRef.current) return;

    wsConnectedRef.current = true;
    connectWebSocketCommands({
      userId: Number(userId),
      getToken: () => localStorage.getItem('caro_auth')
        ? JSON.parse(localStorage.getItem('caro_auth')!).accessToken
        : null,
    });

    return () => {
      disconnectWebSocketCommands();
      wsConnectedRef.current = false;
    };
  }, [isAuthenticated, user]);

  // Push SIP extension updates to MQTT config
  useEffect(() => {
    if (sipExtension && sipExtension !== 'unknown') {
      MqttTelemetry.updateSipExtension(sipExtension);
    }
  }, [sipExtension]);

  // Send alerts on critical state transitions
  useEffect(() => {
    if (prevSipRef.current === 'registered' && sipState === 'disconnected') {
      MqttTelemetry.sendAlert({
        type: 'service_down',
        severity: 'error',
        message: 'SIP registration lost',
        service: 'sip',
      });
    }
    prevSipRef.current = sipState;
  }, [sipState]);

  useEffect(() => {
    if (prevNetworkRef.current && !layers.network) {
      MqttTelemetry.sendAlert({
        type: 'offline',
        severity: 'critical',
        message: 'Network connectivity lost',
        service: 'network',
      });
    }
    prevNetworkRef.current = layers.network;
  }, [layers.network]);

  return { mqttState: connectionState, sessionConflict };
}
